import type { ProjectSummary } from '@paid-ui/queries/search-projects';
import type { Address } from '@paid-ui/schemas/zod/address';

import { formatDayOnly, utc2Mel } from '@paid-ui/utils/datetime';

export const getFullProjectName = (
  projectName: string,
  projectNumber = '',
  address: Address | null = null,
) => {
  const { suburb, state } = address ?? {};
  const suburbState = [suburb, state].filter(Boolean).join(' ');
  const shortProjectName = [projectNumber, projectName].filter(Boolean).join(' · ');
  return [shortProjectName, suburbState].filter(Boolean).join(' · ');
};

export const formatProjectName = (project: ProjectSummary) => {
  const { customName, formattedAddress, projectNumber } = project;
  const projectName = customName ?? formattedAddress;
  return [projectNumber, projectName].filter(Boolean).join(' · ');
};

export interface SplitAddress {
  street: string;
  locality: string;
}

/**
 * Split the one address string the project search endpoint returns into a street line
 * and a locality line. `78 Webb Dock Drive, Port Melbourne VIC 3207` becomes its street
 * and its locality; an address with no comma is all locality, so callers that show only
 * one line still read correctly.
 *
 * @param formattedAddress - Formatted address
 * @returns Street line and locality line
 */
export const splitAddress = (formattedAddress = ''): SplitAddress => {
  const separatorIndex = formattedAddress.indexOf(',');

  if (separatorIndex === -1) {
    return { street: '', locality: formattedAddress.trim() };
  }

  return {
    street: formattedAddress.slice(0, separatorIndex).trim(),
    locality: formattedAddress.slice(separatorIndex + 1).trim(),
  };
};

/**
 * Format a project's creation date, with how long ago it was. The absolute date anchors
 * it and the relative one is what people actually read; created date is also the
 * project list's default sort key, so it is worth showing in full wherever it appears.
 *
 * @param createdAt - UTC creation date
 * @returns Formatted date and relative age, or an empty string when missing
 */
export const formatCreatedAt = (createdAt?: string) => {
  if (!createdAt) {
    return '';
  }

  return `Created ${formatDayOnly(createdAt)} · ${utc2Mel(createdAt).fromNow()}`;
};
